/**
 * Global generation manager — backed by Supabase `generation_jobs` table.
 * Survives page reloads and component unmounts; uses realtime for live updates.
 */
import { supabase } from '@/integrations/supabase/client';
import { logError } from '@/lib/errorLogger';

export type GenerationStatus = 'idle' | 'transcribing' | 'generating' | 'saving' | 'done' | 'error';

export interface GenerationJob {
  id: string;
  url: string;
  contentType: 'sermon' | 'study' | 'music';
  status: GenerationStatus;
  noteId: string | null;
  errorMsg: string | null;
  churchName: string | null;
  isMusic: boolean;
  startedAt: number;
}

type Listener = () => void;

interface JobRow {
  id: string;
  user_id: string;
  url: string;
  content_type: 'sermon' | 'study' | 'music';
  status: 'generating' | 'done' | 'error';
  note_id: string | null;
  church_name: string | null;
  is_music: boolean;
  error_msg: string | null;
  created_at: string;
}

const rowToJob = (r: JobRow): GenerationJob => ({
  id: r.id,
  url: r.url,
  contentType: r.content_type,
  status: r.status === 'generating' ? 'generating' : r.status,
  noteId: r.note_id,
  errorMsg: r.error_msg,
  churchName: r.church_name,
  isMusic: r.is_music,
  startedAt: new Date(r.created_at).getTime(),
});

class GenerationManager {
  private jobs: Map<string, GenerationJob> = new Map();
  private listeners: Set<Listener> = new Set();
  private snapshot: GenerationJob[] = [];
  private channel: ReturnType<typeof supabase.channel> | null = null;
  private currentUserId: string | null = null;

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    // Lazy-initialize realtime sync on first subscriber
    this.ensureSync();
    return () => this.listeners.delete(listener);
  }

  private notify() {
    this.snapshot = Array.from(this.jobs.values()).sort((a, b) => b.startedAt - a.startedAt);
    this.listeners.forEach(fn => fn());
  }

  getJobs(): GenerationJob[] {
    return this.snapshot;
  }

  getActiveJob(): GenerationJob | null {
    for (const job of this.snapshot) {
      if (job.status === 'generating' || job.status === 'transcribing' || job.status === 'saving') {
        return job;
      }
    }
    return null;
  }

  getLatestJob(): GenerationJob | null {
    return this.snapshot[0] ?? null;
  }

  async clearJob(id: string) {
    this.jobs.delete(id);
    this.notify();
    await supabase.from('generation_jobs').delete().eq('id', id);
  }

  /** Ensure we have loaded existing jobs and subscribed to realtime updates */
  private async ensureSync() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    // Re-init if user changed
    if (this.currentUserId === user.id && this.channel) return;
    this.currentUserId = user.id;

    if (this.channel) {
      await supabase.removeChannel(this.channel);
      this.channel = null;
    }

    // Load existing jobs (last 24h, non-cleared)
    const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
    const { data } = await supabase
      .from('generation_jobs')
      .select('*')
      .eq('user_id', user.id)
      .gte('created_at', since)
      .order('created_at', { ascending: false });

    this.jobs.clear();
    for (const row of (data ?? []) as JobRow[]) {
      this.jobs.set(row.id, rowToJob(row));
    }
    this.notify();

    // Subscribe to realtime changes
    this.channel = supabase
      .channel(`generation_jobs_${user.id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'generation_jobs', filter: `user_id=eq.${user.id}` },
        (payload) => {
          if (payload.eventType === 'DELETE') {
            this.jobs.delete((payload.old as any).id);
          } else {
            const row = payload.new as JobRow;
            this.jobs.set(row.id, rowToJob(row));
          }
          this.notify();
        }
      )
      .subscribe();
  }

  /** Start a generation — returns job ID (resolved async after DB insert) */
  async startGeneration(url: string, contentType: 'sermon' | 'study' | 'music'): Promise<string | null> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data, error } = await supabase
      .from('generation_jobs')
      .insert({
        user_id: user.id,
        url,
        content_type: contentType,
        status: 'generating',
        is_music: contentType === 'music',
      })
      .select()
      .single();

    if (error || !data) {
      console.error('Failed to create job:', error);
      return null;
    }

    const job = rowToJob(data as JobRow);
    this.jobs.set(job.id, job);
    this.notify();

    // Fire and forget — runs independently of component lifecycle
    this.executeGeneration(job.id).catch(console.error);

    return job.id;
  }

  private async executeGeneration(jobId: string) {
    const job = this.jobs.get(jobId);
    if (!job) return;

    try {
      // Read the user's preferred language so the AI generates in their language.
      const { data: prof } = await supabase
        .from('profiles')
        .select('language')
        .eq('user_id', (await supabase.auth.getUser()).data.user?.id ?? '')
        .maybeSingle();
      const language = (prof as any)?.language ?? 'pt';
      const body: any = { youtube_url: job.url, user_generated: true, tipo: job.contentType, language };

      // The edge function can take 1–5 minutes (transcription + multi-LLM cascade).
      // Browsers / mobile networks abort fetch around 60s with the unhelpful
      // message "Failed to send a request to the Edge Function", even though the
      // function keeps running on the server. We therefore:
      //  1. Fire the invoke but race it against a soft client timeout.
      //  2. If the network drops first, we DO NOT mark the job as errored —
      //     we simply rely on the realtime subscription on `generation_jobs`,
      //     which the edge function updates when it finishes.
      //  3. We only flip to `error` if the function returns an explicit error,
      //     or if the row is still `generating` after 6 minutes (server cap).
      const SOFT_TIMEOUT_MS = 55_000;
      const HARD_TIMEOUT_MS = 6 * 60_000;

      const invokePromise = supabase.functions
        .invoke('process-youtube', { body })
        .then((res) => ({ kind: 'response' as const, res }))
        .catch((err) => ({ kind: 'network' as const, err }));

      const softTimeout = new Promise<{ kind: 'soft-timeout' }>((resolve) =>
        setTimeout(() => resolve({ kind: 'soft-timeout' }), SOFT_TIMEOUT_MS),
      );

      const first = await Promise.race([invokePromise, softTimeout]);

      // CASE A: function answered within the soft window
      if (first.kind === 'response') {
        const { data, error } = first.res as any;
        if (error) {
          // Network-style errors from supabase-js (e.g. "Failed to send a
          // request to the Edge Function") may fire even when the function is
          // still alive on the server. Treat them as "wait for realtime".
          const msg = (error?.message || '').toLowerCase();
          const looksTransient =
            msg.includes('failed to send a request') ||
            msg.includes('failed to fetch') ||
            msg.includes('network') ||
            msg.includes('timeout') ||
            msg.includes('aborted');
          if (looksTransient) {
            this.waitForJobRow(jobId, HARD_TIMEOUT_MS);
            return;
          }
          throw new Error(error.message || 'Erro ao gerar nota');
        }
        if (data?.needs_manual) throw new Error('Transcrição não disponível para este vídeo.');
        if (data?.error && !data?.success) throw new Error(data.error);
        if (data?.note?.id) {
          await supabase.from('generation_jobs').update({
            status: 'done',
            note_id: data.note.id,
            church_name: data.church_name || null,
            is_music: !!data.is_music || job.contentType === 'music',
          }).eq('id', jobId);
        } else {
          // Function answered but with no note — wait for realtime, the
          // server may still be finalising in another async path.
          this.waitForJobRow(jobId, HARD_TIMEOUT_MS);
        }
        return;
      }

      // CASE B: client lost the fetch (network/abort) — keep waiting via realtime
      if (first.kind === 'network') {
        console.warn('[generation] client fetch dropped, awaiting realtime', first.err);
        this.waitForJobRow(jobId, HARD_TIMEOUT_MS);
        // also fire-and-forget the original promise so any late success still
        // flows through (no further handling — DB row is the source of truth).
        invokePromise.catch(() => {});
        return;
      }

      // CASE C: soft timeout — function still working server-side
      console.info('[generation] soft timeout reached, awaiting realtime');
      this.waitForJobRow(jobId, HARD_TIMEOUT_MS);
      invokePromise.catch(() => {});
    } catch (err: any) {
      console.error('Generation error:', err);
      await logError('generation:process-youtube', err, {
        url: job.url,
        contentType: job.contentType,
        jobId,
      });
      await supabase.from('generation_jobs').update({
        status: 'error',
        error_msg: err.message || 'Erro ao gerar nota',
      }).eq('id', jobId);
    }
  }

  /**
   * Polls the `generation_jobs` row until it reaches a terminal state or the
   * hard timeout elapses. Realtime usually delivers the update first; this is
   * a safety net for when realtime is throttled or the tab was backgrounded.
   */
  private async waitForJobRow(jobId: string, hardTimeoutMs: number) {
    const startedAt = Date.now();
    const POLL_MS = 5_000;
    while (Date.now() - startedAt < hardTimeoutMs) {
      await new Promise((r) => setTimeout(r, POLL_MS));
      const { data } = await supabase
        .from('generation_jobs')
        .select('status, error_msg')
        .eq('id', jobId)
        .maybeSingle();
      if (!data) return;
      if (data.status === 'done' || data.status === 'error') return;
    }
    // Hard timeout — the server clearly failed to update the row.
    await supabase.from('generation_jobs').update({
      status: 'error',
      error_msg: 'A geração demorou mais que o esperado. Tente novamente.',
    }).eq('id', jobId);
  }
}

// Singleton — lives for the entire app session
export const generationManager = new GenerationManager();
